import { SyuugyouBasyoApi } from "@/api/SyuugyouBasyoApi";
import { BaseOptionWithoutCode } from "@/types/Option";
import { SyuugyouBasyo, SyuugyouBasyoAndKoyouKeiyaku, SyuugyouBasyoAndKoyouKeiyakuOption, SyuugyouBasyoOption, SyuugyouBasyoReq } from "@/types/SyuugyouBasyo";
import { SyuugyouBasyo as MstSyuugyouBasyo } from "@/types/KoyouKeiyaku";

export class SyuugyouBasyoService {

  /**
   * マスタに登録されている就業場所のうち、実行ユーザーに閲覧権限のある全ての就業場所を取得します
   */
  public async getAll(): Promise<SyuugyouBasyo[]> {
    const api = new SyuugyouBasyoApi
      ();
    try {
      return await api.getAll();
    } catch (e) {
      throw e;
    }
  }

  /**
   * マスタに登録されている就業場所のうち、実行ユーザーに閲覧権限のある就業場所を取得します（オプション値）
   */
  public async getAllOptions(): Promise<SyuugyouBasyoOption[]> {
    const api = new SyuugyouBasyoApi
      ();
    try {
      return await api.getAllOptions();
    } catch (e) {
      throw e;
    }
  }

  /**
   * 指定したユーザーの有効な雇用契約に基づき就業場所を取得します
   */
  public async getByUserId(userId: number | null, showOldKeiyaku: boolean): Promise<SyuugyouBasyo[]> {
    const api = new SyuugyouBasyoApi();
    const req: SyuugyouBasyoReq = {
      userId,
      month: null,
      year: null,
      showOldKeiyaku,
      showKeiyakuKikanForGetOptions: false,
      targetDate: null,
    }
    try {
      return await api.getByUserId(req);
    } catch (e) {
      throw e;
    }
  }

  /**
   * 指定したユーザーの有効な雇用契約に基づき就業場所を取得します（オプション値）
   */
  public async getOptionsByUserId(userId: number | null, showOldKeiyaku: boolean, showKeiyakuKikanForGetOptions: boolean): Promise<SyuugyouBasyoOption[]> {
    const api = new SyuugyouBasyoApi()
    const req: SyuugyouBasyoReq = {
      userId,
      month: null,
      year: null,
      showOldKeiyaku,
      showKeiyakuKikanForGetOptions,
      targetDate: null,
    }
    try {
      return await api.getOptionsByUserId(req);
    } catch (e) {
      throw e;
    }
  }

  /**
   * 指定したユーザーの有効な雇用契約と打刻履歴に基づき就業場所を取得します
   */
  public async getByDakokuAndKoyouKeiyaku(userId: number | null, year: number | null, month: number | null, showOldKeiyaku: boolean): Promise<SyuugyouBasyo[]> {
    const api = new SyuugyouBasyoApi();
    const req: SyuugyouBasyoReq = {
      userId,
      year,
      month,
      showOldKeiyaku,
      showKeiyakuKikanForGetOptions: false,
      targetDate: null,
    }
    try {
      return await api.getByDakokuAndKoyouKeiyaku(req);
    } catch (e) {
      throw e;
    }
  }

  /**
   * 指定したユーザーの有効な雇用契約と打刻履歴に基づき就業場所を取得します（オプション値）
   */
  public async getOptionsByDakokuAndKoyouKeiyaku(userId: number | null, year: number | null, month: number | null, showOldKeiyaku: boolean, showKeiyakuKikanForGetOptions: boolean): Promise<SyuugyouBasyoOption[]> {
    const api = new SyuugyouBasyoApi()
    const req: SyuugyouBasyoReq = {
      userId,
      year,
      month,
      showOldKeiyaku,
      showKeiyakuKikanForGetOptions,
      targetDate: null,
    }
    try {
      return await api.getOptionsByDakokuAndKoyouKeiyaku(req);
    } catch (e) {
      throw e;
    }
  }

  /**
   *  指定ユーザーの有効な雇用契約に基づく就業場所一覧とその雇用契約で定められた就業時間を取得します
   */
  public async getByUserIdWithKoyouKeiyakuTimes(userId: number | null, showOldKeiyaku: boolean): Promise<SyuugyouBasyoAndKoyouKeiyaku[]> {
    const api = new SyuugyouBasyoApi()
    const req: SyuugyouBasyoReq = {
      userId,
      month: null,
      year: null,
      showOldKeiyaku,
      showKeiyakuKikanForGetOptions: false,
      targetDate: null,
    }
    try {
      return await api.getByUserIdWithKoyouKeiyakuTimes(req);
    } catch (e) {
      throw e;
    }
  }

  /**
   *  指定ユーザーの有効な雇用契約に基づく就業場所一覧とその雇用契約で定められた就業時間を取得します（オプション値）
   */
  public async getOptionByUserIdWithKoyouKeiyakuTimes(userId: number | null, showOldKeiyaku: boolean, showKeiyakuKikanForGetOptions: boolean, targetDate: string | null): Promise<SyuugyouBasyoAndKoyouKeiyakuOption[]> {
    const api = new SyuugyouBasyoApi()
    const req: SyuugyouBasyoReq = {
      userId,
      month: null,
      year: null,
      showOldKeiyaku,
      showKeiyakuKikanForGetOptions,
      targetDate: targetDate,
    }
    try {
      return await api.getOptionByUserIdWithKoyouKeiyakuTimes(req);
    } catch (e) {
      throw e;
    }
  }

  /**
   * マスタに登録されている就業場所のうち、実行ユーザーに閲覧権限のある全ての就業場所を取得します
   */
  public async getNoBelongToBuka(): Promise<SyuugyouBasyo[]> {
    const api = new SyuugyouBasyoApi
      ();
    try {
      return await api.getNoBelongToBuka();
    } catch (e) {
      throw e;
    }
  }

  /**
   * マスタに登録されている就業場所のうち、実行ユーザーに閲覧権限のある就業場所を取得します（オプション値）
   */
  public async getNoBelongToBukaOptions(): Promise<BaseOptionWithoutCode[]> {
    const api = new SyuugyouBasyoApi
      ();
    try {
      return await api.getNoBelongToBukaOptions();
    } catch (e) {
      throw e;
    }
  }

  public async upsertSyuugyouBasyo(syuugyouBasyo: MstSyuugyouBasyo) {
    const api = new SyuugyouBasyoApi();
    try {
      return await api.upsert(syuugyouBasyo);
    } catch (e) {
      throw e;
    }
  }

  public async deleteSyuugyouBasyo(syuugyouBasyoId: number) {
    const api = new SyuugyouBasyoApi();
    try {
      return await api.deleteSyuugyouBasyo(syuugyouBasyoId);
    } catch (e) {
      throw e;
    }
  }

}
